import { lazy, Suspense, useCallback, useEffect, useState, type ReactElement } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { ApiError, apiFetch } from './lib/api';
import type { AuthMe } from './lib/auth-types';
import RailShell from './components/RailShell';
import LockedOutCard from './components/LockedOutCard';
// Auth path stays EAGER: the login screen must not need a second round-trip,
// and it's tiny. Everything behind the session is route-split (React.lazy) so
// the signed-out visitor downloads the shell + auth pages, not the whole app.
import Login from './pages/Login';
import Verify from './pages/Verify';
import Accept from './pages/Accept';
// Eager (not lazy): the /r shared-report view renders SYNCHRONOUSLY in the
// pre-auth short-circuit below, before any Suspense boundary exists.
import SharedReportPage from './pages/SharedReportPage';
import { DateRangeProvider } from './lib/date-range';
// Account-visibility Stage 3: the app-wide "My accounts / All accounts" filter.
// A leaf module (React only) — safe to import eagerly here and from the lazy
// pages; it imports nothing back, so it can never form the import cycle the
// TDZ rule exists to prevent.
import { AccountFilterProvider } from './lib/account-filter';

const HomePage = lazy(() => import('./components/HomePage'));
const ProspectQueue = lazy(() => import('./components/ProspectQueue'));
const Settings = lazy(() => import('./pages/Settings'));
const Overview = lazy(() => import('./pages/Overview'));
const ActivationPage = lazy(() => import('./pages/ActivationPage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const ExperimentsPage = lazy(() => import('./pages/ExperimentsPage'));
const StickinessPage = lazy(() => import('./pages/StickinessPage'));
const UsagePage = lazy(() => import('./pages/UsagePage'));
const InsightsPage = lazy(() => import('./pages/InsightsPage'));
const PaidAdsPage = lazy(() => import('./pages/PaidAdsPage'));
const InfluencersPage = lazy(() => import('./pages/InfluencersPage'));
const MilestonesPage = lazy(() => import('./pages/MilestonesPage'));
const FtuePage = lazy(() => import('./pages/FtuePage'));
const PulsePage = lazy(() => import('./pages/PulsePage'));
const CohortsPage = lazy(() => import('./pages/CohortsPage'));
const OrgsPage = lazy(() => import('./pages/OrgsPage'));
const PipelinePage = lazy(() => import('./pages/PipelinePage'));
const ContractsPage = lazy(() => import('./pages/ContractsPage'));
const ForecastPage = lazy(() => import('./pages/ForecastPage'));
const SalesEnablementPage = lazy(() => import('./pages/SalesEnablementPage'));
const SalesPricingPage = lazy(() => import('./pages/SalesPricingPage'));
const SalesProspectingPage = lazy(() => import('./pages/SalesProspectingPage'));
const CustomersPage = lazy(() => import('./pages/CustomersPage'));
const OrgDetailPage = lazy(() => import('./pages/OrgDetailPage'));
const QbrRisksPage = lazy(() => import('./pages/QbrRisksPage'));
const HubspotCompanyRedirect = lazy(() => import('./pages/HubspotCompanyRedirect'));
const ScoringConfigPage = lazy(() => import('./pages/ScoringConfigPage'));
const HubspotIntegrationsPage = lazy(() => import('./pages/HubspotIntegrationsPage'));
const ToolsPage = lazy(() => import('./pages/ToolsPage'));
const FieldPlanPage = lazy(() => import('./pages/FieldPlanPage'));

type AuthState =
  | { status: 'loading' }
  | { status: 'signed-out' }
  | { status: 'signed-in'; me: AuthMe };

// Public, un-gated shared-report route. Renders entirely from the URL fragment
// (no login, no funnel/API calls) — see pages/SharedReportPage + lib/share-report.
const isSharedRoute = (): boolean =>
  typeof window !== 'undefined' && window.location.pathname.replace(/\/+$/, '') === '/r';

export default function App() {
  const [auth, setAuth] = useState<AuthState>({ status: 'loading' });

  const refresh = useCallback(async () => {
    try {
      const me = await apiFetch<AuthMe>('/api/auth/me');
      setAuth({ status: 'signed-in', me });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setAuth({ status: 'signed-out' });
      } else {
        setAuth({ status: 'signed-out' });
      }
    }
  }, []);

  useEffect(() => {
    if (isSharedRoute()) return; // public share view — never call the funnel
    void refresh();
  }, [refresh]);

  // Public share view short-circuits BEFORE any auth gate: a /r link renders the
  // shared report immediately, signed-out, with no funnel access.
  if (isSharedRoute()) {
    return <SharedReportPage />;
  }

  if (auth.status === 'loading') {
    return (
      <div className="login-shell">
        <div className="subtle">Loading…</div>
      </div>
    );
  }

  if (auth.status === 'signed-out') {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/verify" element={<Verify onVerified={refresh} />} />
        <Route path="/accept" element={<Accept onAccepted={refresh} />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  const me = auth.me;
  const lockedOut = me.lockedOut || me.role === null;

  // Module-gated route element: admins bypass, everyone else needs the grant
  // or lands on the Pulse scoreboard. ONE definition — the per-route inline
  // ternaries kept drifting (/activation shipped ungated while its siblings
  // were gated). Backend requireModule enforces the same gate server-side.
  const gated = (module: string, el: ReactElement) =>
    me.role === 'admin' || me.allowedModules.includes(module) ? (
      el
    ) : (
      <Navigate to="/pulse" replace />
    );

  const content = lockedOut ? (
    <LockedOutCard kind="no_access" email={me.email} />
  ) : (
      <Suspense
        fallback={
          <div className="subtle" style={{ padding: 24 }}>
            Loading…
          </div>
        }
      >
        <Routes>
          {/* Overview / North-Star dashboard — ADMIN-ONLY (user call
              2026-06-12); everyone else lands on the Pulse scoreboard. */}
          <Route
            path="/"
            element={me.role === 'admin' ? <Overview /> : <Navigate to="/pulse" replace />}
          />
          {/* Rail shell's Home → the role-resolved landing (HomePage).
              Marketing / Product / Sales Homes ship; others fall back to the
              default landing. accountScoped scopes the Sales rep's account list. */}
          <Route
            path="/home"
            element={
              <HomePage
                role={me.role}
                allowedModules={me.allowedModules}
                accountScoped={me.accountScoped ?? false}
              />
            }
          />
          <Route path="/pulse" element={<PulsePage />} />

          {/* GTM lifecycle stages — primary nav. Engagement / Expansion /
              Churn / Win-back (placeholder stubs) and Retention Risk
              (seeded-only) were removed until they have trusted data; old
              URLs fall through the catch-all to Overview. */}
          {/* Same 'activation' gate as its Onboarding/Experiments siblings —
              this route was the one of the three a URL could reach ungated. */}
          <Route path="/activation" element={gated('activation', <ActivationPage />)} />
          {/* Onboarding — the clear first-time-user funnel + A/B experiment
              readout (Product hub). Gated on the 'activation' module (admins
              bypass); backend requireAnyModule(['activation']) enforces too. */}
          <Route path="/onboarding" element={gated('activation', <OnboardingPage />)} />
          {/* Experiments — every A/B moxo runs (index + readout). Same gate as
              Onboarding: the readouts are built on the same activation data. */}
          <Route path="/experiments" element={gated('activation', <ExperimentsPage />)} />
          <Route path="/retention" element={<StickinessPage />} />
          {/* Pre-rename URL — keep old bookmarks working */}
          <Route path="/stickiness" element={<Navigate to="/retention" replace />} />
          <Route path="/usage" element={<UsagePage />} />
          {/* Acquisition was folded into the Marketing hub (Channels / By page
              / Content sub-tabs). Old bookmarks land on the hub. */}
          <Route path="/acquisition" element={<Navigate to="/paid-ads" replace />} />
          <Route path="/signups-by-page" element={<Navigate to="/paid-ads" replace />} />
          <Route
            path="/paid-ads"
            element={
              <PaidAdsPage
                allowedModules={me.allowedModules}
                isAdmin={me.role === 'admin'}
              />
            }
          />
          {/* Influencers — the affiliate/partner program, promoted out of the
              Marketing hub's Sources sub-tabs so the campaign surface isn't
              three levels deep. Same 'acquisition' gate as those tabs. */}
          <Route path="/influencers" element={gated('acquisition', <InfluencersPage />)} />

          {/* Milestones — the business north-star reports (Spend Coverage first).
              Finance-sensitive: admins or an explicit 'milestones' grant; others
              land on the Pulse scoreboard. Backend requireModule enforces too. */}
          <Route
            path="/milestones"
            element={gated(
              'milestones',
              <MilestonesPage canEdit={me.role === 'admin' || me.role === 'editor'} />,
            )}
          />

          {/* FTUE Replay Insights — watch first-time journeys at the worst
              funnel drop-off, tag them, and promote corroborated patterns to
              findings. Gated on the 'ftue' module (admins bypass); others land
              on Pulse. Backend requireModule enforces too. */}
          <Route path="/ftue" element={gated('ftue', <FtuePage canAccess />)} />

          {/* AI funnel digest (the weekly analyst). Replaces the old
              retired-URL redirect /insights → /acquisition. */}
          <Route path="/insights" element={<InsightsPage />} />

          {/* Workflow page (CS) */}
          <Route path="/orgs" element={<OrgsPage accountScoped={me.accountScoped ?? false} />} />
          {/* Rep-facing pipeline board (kanban). "My pipeline" when scoped. */}
          <Route path="/pipeline" element={<PipelinePage accountScoped={me.accountScoped ?? false} />} />
          {/* Contract composition (AI credits / discount / terms). Gated on the
              'pipeline' module like the board (admins bypass); others land on
              Pulse. Backend requireModule enforces too. */}
          <Route path="/contracts" element={gated('pipeline', <ContractsPage />)} />
          {/* Forecast — rep/manager quarterly forecast submissions (AI
              pre-drafted deal calls). Gated on the 'forecast' module (admins
              bypass); backend requireModule('forecast') enforces too. */}
          <Route path="/forecast" element={gated('forecast', <ForecastPage />)} />
          {/* Assigned-rep prospecting cockpit. This is intentionally separate
              from /prospect, the BDR's shared unowned-account queue. */}
          <Route
            path="/sales/prospecting"
            element={gated('sales', <SalesProspectingPage />)}
          />
          {/* Sales Enablement — the live Discovery Call Report tool (Sell rail
              leaf). Gated on 'orgs' (the module every GTM persona holds; admins
              bypass). Backend requireModule('orgs') enforces the same gate. */}
          <Route path="/sales-enablement" element={gated('orgs', <SalesEnablementPage />)} />
          <Route path="/sales/pricing" element={gated('orgs', <SalesPricingPage />)} />
          <Route path="/customers" element={<CustomersPage accountScoped={me.accountScoped ?? false} />} />
          {/* BDR Prospect queue (the rail's Prospect › Queue leaf). Gated on the
              'prospect' module; backend requireModule enforces too. */}
          <Route
            path="/prospect"
            element={
              me.role === 'admin' || me.allowedModules.includes('prospect') ? (
                <ProspectQueue />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route path="/orgs/:orgId" element={<OrgDetailPage viewerModules={me.allowedModules} />} />
          <Route path="/qbr/risks" element={gated('orgs', <QbrRisksPage />)} />
          <Route path="/hs/company/:companyId" element={gated('orgs', <HubspotCompanyRedirect />)} />

          {/* Tools — analyst + admin */}
          <Route path="/tools" element={<ToolsPage role={me.role ?? 'viewer'} />} />
          <Route path="/cohorts" element={<CohortsPage />} />
          {me.role === 'admin' && (
            <Route path="/scoring-config" element={<ScoringConfigPage />} />
          )}
          {me.role === 'admin' && (
            <Route path="/integrations/hubspot" element={<HubspotIntegrationsPage />} />
          )}

          {/* Field Plan — Exec / Admin hub only. Seed list, not the org book. */}
          <Route
            path="/field-plan"
            element={
              me.role === 'admin' ? (
                <FieldPlanPage />
              ) : (
                <LockedOutCard kind="admin_only" email={me.email} />
              )
            }
          />

          {/* Settings — admin only. Non-admins fall through to a locked-out card. */}
          <Route
            path="/settings"
            element={
              me.role === 'admin' ? (
                <Settings me={me} allModules={me.allModules} />
              ) : (
                <LockedOutCard kind="admin_only" email={me.email} />
              )
            }
          />

          {/* Accept route is also reachable while signed-in (a stale tab,
              an admin testing the link) — fall through to refresh + redirect. */}
          <Route path="/accept" element={<Accept onAccepted={refresh} />} />

          {/* Redirects from retired URLs so bookmarks don't die */}
          <Route path="/sales" element={<Navigate to="/paid-ads" replace />} />
          {/* /risk + the removed lifecycle stages (/engagement, /expansion,
              /retention-risk, /churn, /win-back) fall through to the catch-all
              → Overview. */}

          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
  );

  const shellProps = {
    email: me.email,
    role: me.role,
    allowedModules: me.allowedModules,
    onSignOut: refresh,
    impersonating: me.impersonating,
  };

  return (
    <DateRangeProvider dataStart={me.dataStart}>
      {/* One store for the "my book vs everyone" bit, so the accounts list,
          search, the boards, QBR, churn-risk and prospecting all agree.
          `accountScoped` (deprecated alias of the DS-6 accountFilter contract)
          is still what tells us the viewer is filterable at all; an
          admin/editor gets no toggle and no `mine` parameter. */}
      <AccountFilterProvider accountScoped={me.accountScoped ?? false}>
        <RailShell {...shellProps}>{content}</RailShell>
      </AccountFilterProvider>
    </DateRangeProvider>
  );
}
